const getValue = (source, key, fallback = "") =>
  source?.[key] ?? source?.[`${key[0].toUpperCase()}${key.slice(1)}`] ?? fallback;

const numericValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

export const getItemHierarchyKey = (item) => {
  const draftItemId = getValue(item, "drafItemId", null);
  if (draftItemId) return `draft-${draftItemId}`;
  const itemId = getValue(item, "itemId", null);
  if (itemId) return String(itemId);
  const approvalItemId = getValue(item, "approvalItemId", null);
  return approvalItemId ? `approval-${approvalItemId}` : "";
};

export const getItemCodeParts = (code) =>
  String(code || "")
    .split(".")
    .map((part) => part.trim())
    .filter(Boolean);

export const getItemCodeLevel = (code) => getItemCodeParts(code).length;

export const compareItemCodeParts = (leftPart = "", rightPart = "") => {
  const leftIsNumber = /^\d+$/.test(leftPart);
  const rightIsNumber = /^\d+$/.test(rightPart);

  if (leftIsNumber && rightIsNumber) {
    return Number(leftPart) - Number(rightPart);
  }

  return leftPart.localeCompare(rightPart, "vi", {
    numeric: true,
    sensitivity: "base",
  });
};

export const compareItemCode = (left, right) => {
  const leftParts = getItemCodeParts(getValue(left, "itemCode"));
  const rightParts = getItemCodeParts(getValue(right, "itemCode"));
  const maxLength = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < maxLength; index += 1) {
    const comparison = compareItemCodeParts(leftParts[index], rightParts[index]);
    if (comparison !== 0) return comparison;
  }

  return Number(getValue(left, "itemId", 0) || 0) - Number(getValue(right, "itemId", 0) || 0);
};

export const sortItemsByCode = (items = []) => [...items].sort(compareItemCode);

export const hasChildItems = (item) =>
  Boolean(getValue(item, "hasChildren", false) || (item?.children && item.children.length > 0));

export const buildItemHierarchy = (items = []) => {
  const nodes = sortItemsByCode(items).map((item) => ({ ...item, children: [] }));
  const nodesById = new Map();
  const nodesByCode = new Map();
  const rootItems = [];

  nodes.forEach((item) => {
    const itemId = getValue(item, "itemId", null);
    const draftItemId = getValue(item, "drafItemId", null);
    if (itemId) nodesById.set(String(itemId), item);
    if (draftItemId) nodesById.set(`draft-${draftItemId}`, item);
    const itemCode = getValue(item, "itemCode", null);
    if (itemCode) nodesByCode.set(String(itemCode), item);
  });

  nodes.forEach((item) => {
    const itemId = getValue(item, "itemId", null);
    const parentItemId = getValue(item, "parentItemId", null);
    const draftParentItemId = getValue(item, "drafParentItemId", null);
    const parentItemCode = getValue(item, "parentItemCode", null);
    const parent =
      (draftParentItemId ? nodesById.get(`draft-${draftParentItemId}`) : null) ||
      (parentItemId ? nodesById.get(String(parentItemId)) : null) ||
      (parentItemCode ? nodesByCode.get(String(parentItemCode)) : null);

    if (parent && getItemHierarchyKey(parent) !== (getItemHierarchyKey(item) || String(itemId || ""))) {
      parent.children.push(item);
      parent.hasChildren = true;
    } else {
      rootItems.push(item);
    }
  });

  const sortNodeChildren = (item) => ({
    ...item,
    children: sortItemsByCode(item.children || []).map(sortNodeChildren),
    hasChildren: hasChildItems(item),
  });

  return sortItemsByCode(rootItems).map(sortNodeChildren);
};

export const getItemLevel = (item) =>
  numericValue(getValue(item, "level_", getValue(item, "level", null))) ??
  Math.max(getItemCodeLevel(getValue(item, "itemCode")) - 1, 0);

export const flattenItemTree = (items = [], level = 0) =>
  (items || []).flatMap((item) => [
    { ...item, level_: getValue(item, "level_", level), hasChildren: hasChildItems(item) },
    ...flattenItemTree(item.children || [], level + 1),
  ]);

export const flattenVisibleItemTree = (items = [], expandedItemIds = new Set(), level = 0) =>
  (items || []).flatMap((item) => [
    { ...item, level_: level, hasChildren: hasChildItems(item) },
    ...(hasChildItems(item) && expandedItemIds.has(getItemHierarchyKey(item))
      ? flattenVisibleItemTree(item.children || [], expandedItemIds, level + 1)
      : []),
  ]);

export const flattenAllItems = (items = []) =>
  (items || []).flatMap((item) => [
    item,
    ...flattenAllItems(item.children || []),
  ]);

export const collectExpandableItemIds = (items = []) =>
  flattenAllItems(items)
    .filter(hasChildItems)
    .map(getItemHierarchyKey);

export const getLeafDescendants = (item) => {
  if (!item) return [];
  if (!item.children || item.children.length === 0) return [item];
  return item.children.flatMap((child) => getLeafDescendants(child));
};
